import { useEffect, useState } from 'react';
import { syncQueue } from '@/lib/syncQueue';
import { WifiOff, RefreshCw } from 'lucide-react';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const updateQueueCount = async () => {
      const count = await syncQueue.count();
      setQueueCount(count);
    };

    updateQueueCount();

    // Subscribe to queue changes
    const unsubscribe = syncQueue.subscribe(() => {
      updateQueueCount();
    });

    return unsubscribe;
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    await syncQueue.sync();
    setIsSyncing(false);
  };

  // Don't show anything if online and queue is empty
  if (isOnline && queueCount === 0) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 ${
        isOnline ? 'bg-blue-500' : 'bg-red-500'
      } text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-in slide-in-from-bottom`}
    >
      {/* Icon */}
      {isOnline ? (
        <RefreshCw className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} />
      ) : (
        <WifiOff className="w-5 h-5" />
      )}

      {/* Message */}
      <div className="flex-1">
        {isOnline ? (
          <div>
            <p className="font-medium text-sm">Syncing...</p>
            <p className="text-xs opacity-90">{queueCount} pending actions</p>
          </div>
        ) : (
          <div>
            <p className="font-medium text-sm">You're offline</p>
            <p className="text-xs opacity-90">
              {queueCount > 0
                ? `${queueCount} actions queued for sync`
                : 'Changes will sync when you\'re back online'}
            </p>
          </div>
        )}
      </div>

      {/* Manual sync button (when online and has items) */}
      {isOnline && queueCount > 0 && (
        <button
          onClick={handleSync}
          disabled={isSyncing}
          className="px-3 py-1 bg-white text-blue-500 rounded text-xs font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSyncing ? 'Syncing...' : 'Sync Now'}
        </button>
      )}
    </div>
  );
}


